import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Button (TASK-661) — the documented action hierarchy, on theme tokens.
 *
 *   PRIMARY     variant="default"      one per view; the main action
 *   SECONDARY   variant="secondary" | "outline"   supporting actions
 *   TERTIARY    variant="ghost" | "link"          low-emphasis / inline actions
 *   DESTRUCTIVE variant="destructive"  irreversible or dangerous actions only
 *   ICON        size="icon"            44×44 CSS-pixel target (WCAG 2.5.5);
 *                                      MUST carry an aria-label.
 *
 * Colors come from the semantic theme tokens (index.css), never raw palette
 * classes — red is reserved for destructive/urgent per the semantic system.
 */

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  children: React.ReactNode;
}

const buttonVariants = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  outline: 'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
  link: 'text-primary underline-offset-4 hover:underline'
};

const buttonSizes = {
  default: 'h-10 px-4 py-2',
  sm: 'h-9 px-3',
  lg: 'h-11 px-8',
  icon: 'h-11 w-11' // 44px minimum interactive target (TASK-661 a11y gate)
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', variant = 'default', size = 'default', children, ...props }, ref) => {
    const variantClass = buttonVariants[variant] || buttonVariants.default;
    const sizeClass = buttonSizes[size] || buttonSizes.default;

    return (
      <button
        className={cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          variantClass,
          sizeClass,
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
